import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { proposalsContent } from "./proposalsData";
import { ProposalDialog } from "./components/ProposalDialog";
import { copyText } from "./lib/copyText";
import { QRCodeSVG } from "qrcode.react";

const PIX_PAYLOAD = "00020126360014br.gov.bcb.pix0114+55119661621715204000053039865802BR5910SERRA NETO6009SAO PAULO62070503***6304D58D";
import { 
  Menu, 
  X, 
  Instagram, 
  Twitter, 
  Phone, 
  Mail, 
  MapPin,
  ChevronRight,
  Users,
  Target,
  Building2,
  Handshake,
  TrendingUp,
  FileText,
  Landmark,
  Store,
  ShieldCheck,
  Sprout,
  Linkedin,
  ClipboardCopy,
  Heart,
  Vote,
  Music,
  Pause,
  Play
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#manifesto", label: "Manifesto" },
    { href: "#sobre", label: "Sobre" },
    { href: "#propostas", label: "Propostas" },
    { href: "#apoie", label: "Apoie" },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="number-badge w-12 h-12 text-sm">3084</div>
            <span className="font-oswald font-bold text-xl text-novo-navy uppercase tracking-tight">
              Serra Neto
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-dm font-medium text-slate-600 hover:text-novo-orange transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsOpen(false)}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
};

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Election date: October 4, 2026
    const electionDate = new Date('2026-10-04T08:00:00');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = electionDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex gap-3" data-testid="countdown-timer">
      {[
        { value: timeLeft.days, label: 'Dias' },
        { value: timeLeft.hours, label: 'Horas' },
        { value: timeLeft.minutes, label: 'Min' },
        { value: timeLeft.seconds, label: 'Seg' }
      ].map((item, index) => (
        <div key={index} className="countdown-box">
          <div className="countdown-number">{String(item.value).padStart(2, '0')}</div>
          <div className="countdown-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
};

// Urna Keypad Animation
const UrnaKeypad = () => {
  const digits = ['3', '0', '8', '4'];
  const [step, setStep] = useState(0);
  const audioCtxRef = React.useRef(null);

  const playUrnaBeep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
        if (ctx.state === 'suspended') return;
      }
      const notes = [
        { freq: 950, start: 0, dur: 0.09 },
        { freq: 950, start: 0.13, dur: 0.09 },
        { freq: 1400, start: 0.26, dur: 0.38 }
      ];
      notes.forEach((n) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = n.freq;
        const t0 = ctx.currentTime + n.start;
        gain.gain.setValueAtTime(0.06, t0);
        gain.gain.exponentialRampToValueAtTime(0.001, t0 + n.dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + n.dur + 0.02);
      });
    } catch (e) {}
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((s) => (s >= 7 ? 0 : s + 1));
    }, 650);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (step === 4) playUrnaBeep();
  }, [step]);

  const shown = Math.min(step, 4);
  const complete = step >= 4;

  return (
    <div className="inline-block bg-novo-navy p-4 shadow-lg" data-testid="urna-keypad">
      <p className="font-dm text-[10px] text-slate-400 uppercase tracking-widest mb-2">
        Deputado Federal · Digite o número
      </p>
      <div className="flex gap-2">
        {digits.map((d, i) => (
          <div
            key={i}
            className={`w-11 h-14 flex items-center justify-center border-2 ${
              i < shown ? 'bg-white border-white' : 'bg-slate-800 border-slate-600'
            } transition-colors duration-150`}
          >
            {i < shown ? (
              <span className="font-oswald text-3xl font-bold text-novo-navy">{d}</span>
            ) : i === shown && !complete ? (
              <span className="w-4 h-0.5 bg-novo-orange animate-pulse"></span>
            ) : null}
          </div>
        ))}
      </div>
      <div className="h-6 mt-2 flex items-center">
        {complete ? (
          <p className="font-oswald text-sm font-bold text-novo-orange uppercase tracking-wide">
            ✔ Serra Neto · Partido Novo
          </p>
        ) : (
          <p className="font-dm text-xs text-slate-500">Aguardando dígitos...</p>
        )}
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="hero-bg min-h-[70vh] flex items-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="z-10 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-novo-navy text-white px-4 py-2 mb-6">
              <span className="font-dm text-sm font-medium uppercase tracking-wider">
                Candidato a Deputado Federal por São Paulo 2026 · Nº 3084
              </span>
            </div>
            
            <h1 className="font-oswald text-5xl md:text-7xl font-bold text-novo-navy leading-none mb-4 animate-fade-up">
              MENOS DEPUTADOS.
            </h1>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold leading-none mb-8 animate-fade-up delay-200">
              MAIS <span className="text-novo-orange">RESULTADOS.</span>
            </h1>
            
            <p className="font-dm text-lg text-slate-600 mb-8 max-w-lg animate-fade-up delay-300">
              Uma Câmara menor, mais representativa e mais cobrada. 
              São Paulo tem 22% dos eleitores do país e só 13% das cadeiras. 
              Com a nova régua, passa a ter 21%, quase exatamente o seu tamanho.
            </p>

            <div className="flex flex-wrap gap-4 mb-12 animate-fade-up delay-400">
              <a 
                href="#apoie" 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="hero-cta-apoie"
              >
                Vote Serra Neto 30 84 <ChevronRight size={18} />
              </a>
              <a 
                href="#propostas" 
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="hero-cta-propostas"
              >
                Ver Propostas
              </a>
            </div>

            {/* Countdown */}
            <div className="animate-fade-up delay-500 flex flex-wrap items-end gap-6">
              <div>
                <p className="font-dm text-sm text-slate-500 uppercase tracking-wider mb-3">
                  Faltam para as eleições 2026
                </p>
                <CountdownTimer />
              </div>
              <UrnaKeypad />
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative z-10 order-1 lg:order-2">
            <div className="relative">
              <div className="hidden lg:block absolute -top-6 -left-6 w-full h-full border-4 border-novo-orange"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/kl7ogpdd_WhatsApp%20Image%202026-06-25%20at%2011.49.06.jpeg"
                alt="Serra Neto"
                className="w-full h-auto object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-6 right-6 bg-white p-3 lg:p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="number-badge w-12 h-12 lg:w-16 lg:h-16 text-sm lg:text-lg">3084</div>
                  <div>
                    <p className="font-oswald font-bold text-novo-navy uppercase">Partido Novo</p>
                    <p className="font-dm text-xs text-novo-orange font-semibold uppercase">Candidato · Nº 3084</p>
                    <p className="font-dm text-sm text-slate-500">São Paulo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Manifesto Section
const ManifestoSection = () => {
  return (
    <section id="manifesto" className="py-12 lg:py-20 bg-novo-navy noise-overlay" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
              Manifesto
            </span>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase mt-4 mb-8">
              Por que estamos aqui
            </h2>

            <div className="space-y-6">
              <p className="font-dm text-xl text-slate-300 leading-relaxed">
                O Brasil é um país que trabalha muito <br className="hidden md:block" />
                <span className="text-white font-semibold">e anda pouco.</span>
              </p>
              
              <div className="font-dm text-lg text-slate-400 space-y-2">
                <p>Quem quer abrir um negócio enfrenta fila, papel, taxa e carimbo.</p>
                <p>Quem quer produzir paga imposto demais.</p>
                <p>Quem quer investir vive com medo de a regra mudar.</p>
              </div>

              <div className="py-4">
                <p className="font-oswald text-2xl text-white uppercase">Isso não é normal.</p>
                <p className="font-oswald text-2xl text-novo-orange uppercase">Isso não é justo.</p>
              </div>

              <div className="font-dm text-lg text-slate-400 space-y-4">
                <p>
                  Mas tem uma coisa antes de todas essas: <span className="text-white font-semibold">quem decide.</span> Toda 
                  regra que pesa nas suas costas foi votada por alguém. São 513 deputados federais, e quase ninguém 
                  sabe dizer o nome do seu. Você paga por eles e não recebe prestação de contas.
                </p>
                <p>
                  Eu proponho uma <span className="text-white font-semibold">Câmara de 168 deputados</span>, com uma régua pública: 
                  um deputado para cada milhão de eleitores, mínimo de um por estado, recalculada a cada eleição. 
                  São Paulo tem 22% dos eleitores do país e só 13% das cadeiras. Com a régua, passa a ter 21% — o seu 
                  tamanho real. É menos deputado e mais força para quem mora aqui, ao mesmo tempo.
                </p>
                <p className="text-white font-semibold">Não é para economizar. É para que dê para cobrar.</p>
              </div>

              <div className="bg-white/5 p-6 border-l-4 border-novo-orange">
                <p className="font-dm text-lg text-white leading-relaxed">
                  Por isso peço seu voto: <br />
                  <span className="font-oswald font-bold text-novo-orange text-2xl uppercase">Serra Neto, 3084.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="hidden lg:block">
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/hzt0s7zj_WhatsApp%20Image%202026-08-11%20at%2010.26.21.jpeg"
              alt="Serra Neto"
              className="w-full h-auto object-cover rounded-sm shadow-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="sobre" className="py-12 lg:py-20 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/lbuoei5l_WhatsApp%20Image%202026-06-25%20at%2011.49.061.jpeg"
              alt="Serra Neto em ação"
              className="w-full h-auto object-cover"
              data-testid="about-image"
            />
            <div className="absolute -bottom-6 right-0 lg:-right-6 bg-novo-orange p-8 text-white">
              <p className="font-oswald text-5xl font-bold">20+</p>
              <p className="font-dm text-sm uppercase tracking-wider">Anos de experiência</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
              Quem é Serra Neto
            </span>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4 mb-6">
              Renovação de Verdade
            </h2>
            <p className="font-dm text-lg text-slate-600 mb-4 leading-relaxed">
              Sou alguém que decidiu transformar indignação em ação.
            </p>
            <p className="font-dm text-base text-slate-600 mb-4 leading-relaxed">
              Minha trajetória política nasceu observando de perto as dificuldades do trabalhador, 
              do pequeno empreendedor e de quem sustenta o país enfrentando impostos altos, 
              burocracia e promessas que nunca saem do papel.
            </p>
            <p className="font-dm text-base text-slate-600 mb-4 leading-relaxed">
              Ao longo do caminho, participei de movimentos, debates e ações de rua, ouvindo de 
              perto a população, e entendendo que o maior problema do Brasil não é falta de gente 
              trabalhadora. É um sistema pesado, caro e distante de quem produz.
            </p>
            <p className="font-dm text-base text-slate-600 mb-4 leading-relaxed">
              Sou empresário e defensor da liberdade econômica. Conheço de perto os desafios de 
              quem enfrenta um Estado inchado e ineficiente. Trabalhei mais de 20 anos em gestão 
              em multinacionais. Também vivi dez anos fora do Brasil, acompanhando um sistema bem 
              diferente do nosso. Foi ali que a conta ficou clara para mim: <span className="font-semibold text-novo-navy">não 
              falta dinheiro no Brasil, falta quem responda pelo que faz com ele.</span>
            </p>
            <p className="font-dm text-lg text-slate-700 font-semibold mb-8 leading-relaxed">
              Não prometemos mágica. Prometemos trabalho. <br />
              Não prometemos milagre. Prometemos gestão.
            </p>

            {/* Compromissos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="stat-item">
                <p className="font-oswald text-xl font-bold text-novo-navy uppercase">Menos deputados</p>
              </div>
              <div className="stat-item">
                <p className="font-oswald text-xl font-bold text-novo-navy uppercase">Mais eficiência</p>
              </div>
              <div className="stat-item">
                <p className="font-oswald text-xl font-bold text-novo-navy uppercase">Mais segurança</p>
              </div>
              <div className="stat-item">
                <p className="font-oswald text-xl font-bold text-novo-navy uppercase">Menos privilégio</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Proposals Section
const ProposalsSection = () => {
  const [selectedProposal, setSelectedProposal] = useState(null);

  const openProposal = (id) => {
    const content = proposalsContent[id];
    if (content) setSelectedProposal({ id, ...content });
  };

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('proposta');
    if (id && proposalsContent[id]) {
      setSelectedProposal({ id, ...proposalsContent[id] });
      document.getElementById('propostas')?.scrollIntoView();
    }
  }, []);

  const proposals = [
    {
      icon: <Landmark size={32} />,
      title: "Menos Deputados",
      description: "Uma Câmara de 513 para 168, por uma régua pública: um deputado para cada milhão de eleitores, mínimo de um por estado, recalculada a cada eleição. São Paulo tem 22% dos eleitores do país e só 13% dos deputados. Passa a ter 21% dos deputados.",
      detail: "De 513 para 168. E São Paulo pesa mais.",
      link: "https://customer-assets-agu9un31.emergentagent.net/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/5uxapi54_Proposta_A_Conta_da_Camara_v3_com_Plebiscito_03082026_1.pdf",
      onepager: "camara",
    },
    {
      icon: <Store size={32} />,
      title: "Empreendedor Urbano",
      description: "A lei que dispensa licença para quem trabalha por conta própria é de 2019. Em 2021 o Supremo derrubou a cobrança de cadastro por quem vem de outra cidade. Municípios continuam cobrando, porque descumprir não custa nada.",
      detail: "A lei já existe. Falta ela valer.",
      link: "https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/skuz9c59_One%20Pager%20Empreendedor%20Urbano.pdf",
      onepager: "empreendedor-urbano",
    },
    {
      icon: <ShieldCheck size={32} />,
      title: "Segurança com Inteligência",
      description: "Segurança preditiva: dados, integração e tecnologia que já existem, respeitando a lei. Chegar antes, não só reagir depois.",
      detail: "Estado mais inteligente, não maior.",
      link: "https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/42kf6csx_One%20Pager%20Seguran%C3%A7a.pdf",
      onepager: "seguranca",
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Gestão que Presta Contas",
      description: "O Estado gasta o seu dinheiro todos os anos sem nenhuma obrigação legal de medir se aquilo entregou alguma coisa. Meta pública para o alto escalão, teto para cargos sem concurso e verba de campanha menor para o governo que fecha no vermelho.",
      detail: "Gastar não é entregar.",
      link: "https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/9vixkjqn_One%20Pager%20Gest%C3%A3o.pdf",
      onepager: "gestao",
    },
    {
      icon: <Sprout size={32} />,
      title: "Merenda do Produtor Local",
      description: "A lei já obriga que 45% da merenda escolar venha da agricultura familiar. O que falta é consequência: no piso anterior, quase 1.600 municípios ficaram abaixo da meta.",
      detail: "Comida na escola, renda na cidade.",
      link: "https://customer-assets.emergentagent.com/job_5d4063b7-df2b-4aaf-b655-8252d6c6fbb2/artifacts/6am04dyw_One%20Pager%20Agricultura%20Familiar.pdf",
      onepager: "agricultura-familiar",
    }
  ];

  return (
    <section id="propostas" className="py-12 lg:py-20 bg-slate-50" data-testid="proposals-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
            Nossa Proposta
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4">
            Governo Simples, Direto e que Funcione
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {proposals.map((proposal, index) => (
            <button
              key={index}
              type="button"
              onClick={() => openProposal(proposal.onepager)}
              data-onepager={proposal.onepager}
              className="proposal-card flex flex-col text-left cursor-pointer hover:-translate-y-1 hover:shadow-xl transition-all duration-200"
              data-testid={`proposal-card-${proposal.onepager}`}
            >
              <div className="text-novo-orange mb-4">{proposal.icon}</div>
              <h3 className="font-oswald text-xl font-bold text-novo-navy uppercase mb-3">
                {proposal.title}
              </h3>
              <p className="font-dm text-slate-600 text-sm mb-3">{proposal.description}</p>
              <p className="font-dm text-novo-orange font-semibold text-sm mb-4">{proposal.detail}</p>
              <span className="mt-auto inline-flex items-center gap-1 font-dm text-sm font-bold text-novo-navy uppercase tracking-wider group-hover:gap-2">
                Ver a proposta <ChevronRight size={16} />
              </span>
            </button>
          ))}
        </div>

        <ProposalDialog proposal={selectedProposal} onClose={() => setSelectedProposal(null)} />

        {/* Visão */}
        <div className="mt-10 bg-novo-navy p-6 md:p-8">
          <h3 className="font-oswald text-2xl md:text-3xl font-bold text-white uppercase mb-6 text-center">
            Nossa Visão
          </h3>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {['mais representação', 'mais eficiência', 'mais segurança', 'mais renovação', 'menos privilégio'].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-novo-orange">✔</span>
                <span className="font-dm text-white uppercase tracking-wider text-sm">{item}</span>
              </div>
            ))}
          </div>
          <p className="font-oswald text-2xl md:text-3xl font-bold text-novo-orange uppercase mt-8 text-center">
            Menos Deputados. Mais Resultados.
          </p>
        </div>
      </div>
    </section>
  );
};

// Plebiscito Section
const PlebiscitoSection = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const timeline = [
    {
      year: "2027",
      title: "A PEC entra",
      text: "Protocolo com 171 assinaturas e, em paralelo, a proposta de convocação da consulta popular. Nenhum dos dois depende de um deputado sozinho."
    },
    {
      year: "2028",
      title: "O país decide",
      text: "Plebiscito no mesmo dia da eleição municipal: a urna já está na rua e o custo despenca. Enquanto o país responde, a PEC fica suspensa por determinação legal."
    },
    {
      year: "2030",
      title: "A nova Câmara",
      text: "Promulgação até outubro de 2029 e a nova composição já vale na eleição. Depois dessa data, não alcança mais 2030."
    }
  ];

  return (
    <section id="plebiscito" className="py-12 lg:py-20 bg-white" data-testid="plebiscito-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 max-w-4xl mx-auto">
          <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
            Como isso sai do papel
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4 mb-6">
            A PEC é o destino. O plebiscito é o teste de coragem
          </h2>
          <p className="font-dm text-lg text-slate-600 leading-relaxed">
            "Deputado nenhum vai votar pela própria demissão." Está certo. É por isso que a proposta 
            não é uma peça só. A emenda constitucional é o destino: é ela que muda o número de cadeiras. 
            O plebiscito é o que tira do parlamentar o conforto de decidir sozinho, no escuro, sobre o 
            próprio emprego. Depois que o país responde na urna, cada voto contra passa a ter dono e endereço.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {timeline.map((step, i) => (
            <div key={i} className="bg-slate-50 border-t-4 border-novo-orange p-8" data-testid={`plebiscito-card-${step.year}`}>
              <p className="font-oswald text-4xl font-bold text-novo-orange mb-2">{step.year}</p>
              <h3 className="font-oswald text-xl font-bold text-novo-navy uppercase mb-3">{step.title}</h3>
              <p className="font-dm text-slate-600 text-sm leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>

        <div className="relative bg-novo-navy border-4 border-novo-orange p-8 md:p-12 text-center overflow-hidden" data-testid="plebiscito-cta-block">
          <div className="absolute top-0 left-0 w-24 h-24 bg-novo-orange/20 -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-novo-orange/20 translate-x-1/2 translate-y-1/2 rotate-45"></div>
          <p className="font-oswald text-2xl md:text-3xl font-bold text-white uppercase leading-snug max-w-3xl mx-auto">
            O plebiscito não aprova a emenda. Ele obriga cada deputado a votar 
            depois de o país já ter dito o que quer.
          </p>
          <p className="font-oswald text-2xl md:text-3xl font-bold text-novo-orange uppercase mt-2">
            É por isso que ele é um teste de coragem.
          </p>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="mt-8 inline-flex items-center gap-3 bg-novo-orange text-white px-12 py-5 text-lg md:text-xl font-oswald font-bold uppercase tracking-wide shadow-[0_0_35px_rgba(255,88,0,0.55)] hover:scale-105 hover:shadow-[0_0_50px_rgba(255,88,0,0.75)] transition-all duration-200"
            data-testid="plebiscito-details-button"
          >
            Ver o caminho completo <ChevronRight size={24} />
          </button>
          <p className="font-dm text-sm text-slate-400 mt-4">
            O passo a passo com datas, artigos de lei e prazos — de 2027 até a nova Câmara em 2030
          </p>
        </div>

        {dialogOpen && (
          <ProposalDialog
            proposal={{ id: 'plebiscito', ...proposalsContent['plebiscito'] }}
            onClose={() => setDialogOpen(false)}
          />
        )}
      </div>
    </section>
  );
};

// Support Section
const SupportSection = () => {
  return (
    <section id="apoie" className="py-12 lg:py-20 bg-white" data-testid="support-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
          Faça Parte
        </span>
        <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4 mb-6">
          Apoie Nossa Campanha
        </h2>
        <p className="font-dm text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto">
          Junte-se a quem acredita em uma política diferente. 
          Seu voto muda quem decide: <span className="font-bold text-novo-navy">Serra Neto, 3084.</span>
        </p>

        {/* Apoiar / Doações CTA */}
        <div className="mb-12">
          <a
            href="https://queroapoiar.com.br/serraneto"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-novo-orange text-white px-10 py-5 text-lg font-oswald font-bold uppercase tracking-wide hover:scale-105 transition-transform duration-200 shadow-lg"
            data-testid="support-queroapoiar"
          >
            <Handshake size={28} />
            Quero Apoiar e Doar
            <ChevronRight size={22} />
          </a>
          <p className="font-dm text-sm text-slate-500 mt-3">
            Apoie e contribua através da plataforma Quero Apoiar
          </p>

          {/* PIX */}
          <div className="max-w-xl mx-auto mt-8 bg-slate-50 border border-slate-200 p-6" data-testid="pix-info">
            <p className="font-oswald text-lg font-bold text-novo-navy uppercase mb-4">Doe via PIX</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="bg-white p-3 border border-slate-200" data-testid="pix-qrcode">
                <QRCodeSVG value={PIX_PAYLOAD} size={140} level="M" />
                <p className="font-dm text-[10px] text-slate-400 mt-2">Aponte a câmera do celular</p>
                <p className="font-dm text-[10px] text-slate-500 font-semibold">Chave: (11) 96616-2171</p>
              </div>
              <div className="flex flex-col items-center sm:items-start gap-3">
                <p className="font-dm text-xl font-bold text-novo-orange" data-testid="pix-key">(11) 96616-2171</p>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyText('+5511966162171');
                    toast[ok ? 'success' : 'error'](ok ? 'Chave PIX copiada!' : 'Não foi possível copiar');
                  }}
                  className="inline-flex items-center gap-2 bg-novo-navy text-white px-4 py-2 text-sm font-dm font-semibold hover:opacity-90 transition-opacity duration-200"
                  data-testid="pix-copy-button"
                >
                  <ClipboardCopy size={16} /> Copiar chave
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyText(PIX_PAYLOAD);
                    toast[ok ? 'success' : 'error'](ok ? 'PIX copia e cola copiado!' : 'Não foi possível copiar');
                  }}
                  className="inline-flex items-center gap-2 bg-white border border-novo-navy text-novo-navy px-4 py-2 text-sm font-dm font-semibold hover:bg-slate-100 transition-colors duration-200"
                  data-testid="pix-copy-payload-button"
                >
                  <ClipboardCopy size={16} /> PIX copia e cola
                </button>
              </div>
            </div>
            <p className="font-dm text-xs text-slate-500 mt-4 leading-relaxed">
              Doações só de pessoa física, com CPF do doador. Pessoa jurídica não pode doar para campanha. 
              Toda doação recebida gera recibo eleitoral e é declarada à Justiça Eleitoral.
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          <a 
            href="https://instagram.com/serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gradient-to-br from-purple-600 to-pink-500 text-white px-6 py-4 hover:scale-105 transition-transform duration-200"
            data-testid="support-instagram"
          >
            <Instagram size={28} />
            <span className="font-dm font-bold uppercase">Instagram</span>
          </a>
          <a 
            href="https://tiktok.com/@serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black text-white px-6 py-4 hover:scale-105 transition-transform duration-200"
            data-testid="support-tiktok"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
            <span className="font-dm font-bold uppercase">TikTok</span>
          </a>
          <a 
            href="https://x.com/serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black text-white px-6 py-4 hover:scale-105 transition-transform duration-200"
            data-testid="support-x"
          >
            <Twitter size={28} />
            <span className="font-dm font-bold uppercase">X (Twitter)</span>
          </a>
        </div>

        {/* WhatsApp CTA */}
        <a 
          href="https://wa.me/5511985306120" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 hover:scale-105 transition-transform duration-200"
          data-testid="support-whatsapp"
        >
          <Phone size={28} />
          <span className="font-dm font-bold uppercase">Fale pelo WhatsApp</span>
        </a>

        {/* Info */}
        <div className="mt-10 flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-3">
            <Users size={24} className="text-novo-orange" />
            <span className="font-dm text-slate-600">Seja Voluntário</span>
          </div>
          <div className="flex items-center gap-3">
            <Handshake size={24} className="text-novo-orange" />
            <span className="font-dm text-slate-600">Declare seu Apoio</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Blog/News Section
const NewsSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // First seed the blog
        await axios.post(`${API}/blog/seed`);
        // Then fetch posts
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      }
    };
    fetchPosts();
  }, []);

  const defaultPosts = [
    {
      id: '1',
      title: 'Lançamento da Pré-Campanha em São Paulo',
      excerpt: 'Serra Neto inicia sua jornada rumo a Brasília com evento histórico na capital paulista.',
      image_url: 'https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?w=800',
      category: 'Eventos',
      published_at: new Date().toISOString()
    },
    {
      id: '2',
      title: 'Propostas para Desburocratização do Estado',
      excerpt: 'Conheça as principais propostas de Serra Neto para reduzir a burocracia estatal.',
      image_url: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800',
      category: 'Propostas',
      published_at: new Date().toISOString()
    },
    {
      id: '3',
      title: 'Serra Neto Visita Comunidades do Interior',
      excerpt: 'Pré-candidato percorre cidades do interior de SP ouvindo demandas da população.',
      image_url: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800',
      category: 'Campanha',
      published_at: new Date().toISOString()
    }
  ];

  const displayPosts = posts.length > 0 ? posts : defaultPosts;

  return (
    <section id="noticias" className="py-20 lg:py-32 bg-white" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
            Fique por Dentro
          </span>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4">
            Últimas Notícias
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {displayPosts.slice(0, 3).map((post, index) => (
            <article key={post.id} className="blog-card" data-testid={`news-card-${index}`}>
              <div className="overflow-hidden">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="p-6">
                <span className="font-dm text-xs font-bold uppercase text-novo-orange tracking-wider">
                  {post.category}
                </span>
                <h3 className="font-oswald text-xl font-bold text-novo-navy uppercase mt-2 mb-3">
                  {post.title}
                </h3>
                <p className="font-dm text-slate-600 text-sm mb-4">
                  {post.excerpt}
                </p>
                <button className="font-dm text-sm font-bold text-novo-orange uppercase tracking-wider flex items-center gap-1 hover:gap-2 transition-all duration-200">
                  Ler Mais <ChevronRight size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contato" className="py-12 lg:py-20 bg-slate-50" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="font-dm text-novo-orange font-medium uppercase tracking-wider text-sm">
          Entre em Contato
        </span>
        <h2 className="font-oswald text-4xl md:text-5xl font-bold text-novo-navy uppercase mt-4 mb-6">
          Fale Conosco
        </h2>
        <p className="font-dm text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
          Quer saber mais sobre nossas propostas? Tem alguma sugestão? 
          Entre em contato através das nossas redes sociais!
        </p>

        {/* Contact Options */}
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          <a 
            href="https://wa.me/5511985306120" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 hover:scale-105 transition-transform duration-200"
            data-testid="contact-whatsapp"
          >
            <Phone size={24} />
            <div className="text-left">
              <p className="font-dm font-bold">WhatsApp</p>
              <p className="font-dm text-sm opacity-90">(11) 98530-6120</p>
            </div>
          </a>
          <a 
            href="https://instagram.com/serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gradient-to-br from-purple-600 to-pink-500 text-white px-6 py-4 hover:scale-105 transition-transform duration-200"
            data-testid="contact-instagram"
          >
            <Instagram size={24} />
            <div className="text-left">
              <p className="font-dm font-bold">Instagram</p>
              <p className="font-dm text-sm opacity-90">@serraneto30</p>
            </div>
          </a>
        </div>

        {/* More Social Links */}
        <div className="flex justify-center gap-4">
          <a 
            href="https://tiktok.com/@serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-14 h-14 bg-black flex items-center justify-center text-white hover:scale-110 transition-transform duration-200"
            data-testid="contact-tiktok"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          <a 
            href="https://x.com/serraneto30" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-14 h-14 bg-black flex items-center justify-center text-white hover:scale-110 transition-transform duration-200"
            data-testid="contact-x"
          >
            <Twitter size={24} />
          </a>
        </div>

        {/* Location */}
        <div className="mt-10 flex items-center justify-center gap-2 text-slate-500">
          <MapPin size={20} />
          <span className="font-dm">São Paulo, SP</span>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer-section py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & Info */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="number-badge w-16 h-16 text-lg">3084</div>
              <div>
                <p className="font-oswald text-2xl font-bold text-white uppercase">Serra Neto</p>
                <p className="font-dm text-xs text-novo-orange font-semibold uppercase">Candidato a Deputado Federal · Nº 3084</p>
                <p className="font-dm text-sm text-slate-400">Partido Novo • São Paulo</p>
              </div>
            </div>
            <p className="font-oswald text-xl text-novo-orange font-bold uppercase mb-4">
              Menos Deputados. Mais Resultados.
            </p>
            <p className="font-dm text-slate-400 max-w-md">
              Uma Câmara menor, mais representativa e mais cobrada. Menos deputado e mais força para São Paulo.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-oswald text-lg font-bold text-white uppercase mb-4">Links Rápidos</h4>
            <ul className="space-y-2">
              {['Sobre', 'Propostas', 'Apoie', 'Contato'].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase()}`}
                    className="font-dm text-slate-400 hover:text-novo-orange transition-colors duration-200"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-oswald text-lg font-bold text-white uppercase mb-4">Redes Sociais</h4>
            <div className="flex gap-3">
              <a 
                href="https://instagram.com/serraneto30" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-novo-orange transition-colors duration-200"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://tiktok.com/@serraneto30" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-novo-orange transition-colors duration-200"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://x.com/serraneto30" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-novo-orange transition-colors duration-200"
              >
                <Twitter size={20} />
              </a>
              <a 
                href="https://www.linkedin.com/in/casn/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-novo-orange transition-colors duration-200"
                data-testid="footer-linkedin"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://wa.me/5511985306120" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 flex items-center justify-center text-white hover:bg-novo-orange transition-colors duration-200"
                data-testid="footer-whatsapp"
              >
                <Phone size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col items-center gap-2 text-center">
          <p className="font-dm text-sm text-slate-400" data-testid="footer-candidate-info">
            SERRA NETO · Candidato a Deputado Federal · São Paulo 2026 · Partido Novo
          </p>
          <p className="font-dm text-xs text-slate-500">
            CNPJ de campanha 68.236.042/0001-60 · coordenacao@serraneto30.com.br
          </p>
        </div>
      </div>
    </footer>
  );
};

// Music Player Component
const MusicPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const audioRef = React.useRef(null);

  const tracks = [
    {
      title: "Voto Certo - Serra Neto",
      url: "https://cdn1.suno.ai/7971076b-39bd-4464-869f-9ab955808646.mp3"
    }
  ];

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.log('Audio play error:', e));
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const startOnInteraction = () => {
      if (audio.paused) {
        audio.play().catch(() => {});
      }
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('click', startOnInteraction);
      window.removeEventListener('touchstart', startOnInteraction);
      window.removeEventListener('scroll', startOnInteraction);
      window.removeEventListener('keydown', startOnInteraction);
    };

    audio.volume = 0.5;
    audio.play().catch(() => {
      window.addEventListener('click', startOnInteraction);
      window.addEventListener('touchstart', startOnInteraction);
      window.addEventListener('scroll', startOnInteraction);
      window.addEventListener('keydown', startOnInteraction);
    });

    return cleanup;
  }, []);

  const nextTrack = () => {
    setCurrentTrack((prev) => (prev + 1) % tracks.length);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.load();
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-50" data-testid="music-player">
      <audio
        ref={audioRef}
        src={tracks[currentTrack].url}
        onEnded={nextTrack}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <div className="flex flex-col items-end gap-2">
        {isPlaying && (
          <div className="bg-novo-navy text-white px-3 py-2 rounded shadow-lg text-xs max-w-[180px]">
            <p className="font-semibold truncate">{tracks[currentTrack].title}</p>
            {tracks.length > 1 && (
              <button 
                onClick={nextTrack}
                className="text-novo-orange text-xs hover:underline mt-1"
              >
                Próxima →
              </button>
            )}
          </div>
        )}
        <button
          onClick={togglePlay}
          className={`w-14 h-14 ${isPlaying ? 'bg-novo-orange' : 'bg-novo-navy'} text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200`}
          title={isPlaying ? "Pausar música" : "Tocar música da campanha"}
        >
          {isPlaying ? (
            <Pause size={24} />
          ) : (
            <Music size={24} />
          )}
        </button>
      </div>
    </div>
  );
};

// Donate Float Button
const DonateFloat = () => {
  return (
    <a
      href="#apoie"
      className="fixed bottom-6 left-6 z-50 inline-flex items-center gap-2 bg-novo-orange text-white px-5 py-3 font-oswald font-bold uppercase tracking-wide shadow-[0_4px_20px_rgba(255,88,0,0.5)] hover:scale-105 transition-transform duration-200"
      data-testid="donate-float"
      aria-label="Doar para a campanha"
    >
      <Heart size={18} fill="currentColor" /> Doar
    </a>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/5511985306120"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-float"
      aria-label="Contato via WhatsApp"
    >
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main Home Component
const Home = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <ManifestoSection />
      <AboutSection />
      <ProposalsSection />
      <PlebiscitoSection />
      <SupportSection />
      <ContactSection />
      <Footer />
      <MusicPlayer />
      <DonateFloat />
      <WhatsAppFloat />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
